// The top-left readouts: what you have, and what the run has destroyed
// (PLAN-UI §28.3, screen-mock `#readouts`).
//
// Two pills, and the left corner is the one part of the screen no piece stands
// on. **Stock is a glance and its expansion is the reading** — the pill says
// how much of each material there is, hovering it says where all of it is —
// which is the split the docked shelf could not make, because a shelf is always
// open and therefore always at its longest.
//
// Registered with the inset registry on the `top` edge, so the camera knows
// what this covers (PLAN-UI §28.6).
//
// Reads `SessionView.readouts` (PLAN-CLIENT-SPLIT day 8): every number and
// every sentence here is the engine's. What the view does not carry — how many
// warehouses, what is lying on the ground — is counted off `board.facts()`,
// which is a reading of the board rather than a rule.

import { useEffect, useRef, useState } from "react";

import type { ArmyReadout, BoardFacts, Holding, Side } from "src/api";
import { armyTone } from "./armyTone";
import {
  keyframes,
  Panel,
  Pill,
  PillLabel,
  StatCounter,
  styled,
  useChromeRect
} from "./kit";
import { holdingTone } from "./unitTone";
import { useGame } from "./useGame";
import { useSessionView } from "./useSessionView";

/** How long a `+N` stays up. Long enough to read, short enough to overlap. */
const FLOAT_MS = 1100;

/**
 * Scoped through the `keyframes` helper, never as an `@keyframes` key in `sx` —
 * emotion scopes the name and a bare block silently never applies.
 */
const rise = keyframes`
  0%   { opacity: 0; transform: translateY(6px) scale(0.9); }
  18%  { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-26px) scale(1); }
`;

/** The total itself, acknowledging that it moved. */
const bump = keyframes`
  0%   { transform: scale(1); }
  35%  { transform: scale(1.22); }
  100% { transform: scale(1); }
`;

interface Float {
  id: number;
  amount: number;
}

/**
 * The `+N`s in flight.
 *
 * Its own state rather than a diff computed during render: the score is a
 * number and two captures worth the same amount in a row produce the same
 * number twice, so what makes each one an *event* is that something recorded it
 * when it arrived.
 *
 * **Rises only.** Undo is truncate-and-replay, so the score genuinely falls —
 * and a `-9` floating up out of the corner would read as the enemy scoring.
 * Restart takes it to nought the same way.
 */
function useScoreFloats(score: number): Float[] {
  const [floats, setFloats] = useState<Float[]>([]);
  // The previous score, and the id to hand the next float. Refs because neither
  // is drawn — writing them through state would render twice per capture.
  const previous = useRef(score);
  const nextId = useRef(0);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const gained = score - previous.current;
    previous.current = score;
    if (gained <= 0) return;

    const id = nextId.current++;
    setFloats((up) => [...up, { id, amount: gained }]);
    // **No per-effect cleanup here**, which is the obvious shape and is wrong:
    // this effect re-runs on the next capture, so its cleanup would cancel the
    // *previous* float's timer — and that float would then stay on screen for
    // the rest of the run. Two captures inside the window is the ordinary case.
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setFloats((up) => up.filter((one) => one.id !== id));
    }, FLOAT_MS);
    timers.current.add(timer);
  }, [score]);

  // Unmount is the one place they are all cancelled together.
  const pending = timers.current;
  useEffect(
    () => () => {
      for (const timer of pending) clearTimeout(timer);
      pending.clear();
    },
    [pending]
  );

  return floats;
}

const Row = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 14,
  top: 12,
  // **`popover`, not `chrome`.** The stock pill's hover expansion is a popover
  // in all but mechanism and it drops straight down over the hotbar, which
  // shares the left edge and is a later sibling on the `chrome` rung — so on
  // that rung the panel opens *behind* the tiles it covers.
  zIndex: theme.kit.z.popover,
  display: "flex",
  gap: 9,
  alignItems: "flex-start"
}));

/** The pill and its expansion, so one hover — or one focus — covers both. */
const StockWrap = styled("div")({
  position: "relative",
  "&:hover .stock-detail, &:focus-within .stock-detail": { display: "block" }
});

const StockPill = styled(Pill)(({ theme }) => ({
  cursor: "default",
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  }
}));

const Detail = styled(Panel)({
  display: "none",
  position: "absolute",
  left: 0,
  // Flush with the pill's bottom edge and bridged across the gap below it, or
  // the cursor leaves the wrapper on its way down and the panel it was
  // heading for closes under it.
  top: "100%",
  marginTop: 6,
  width: 290,
  padding: "12px 14px",
  "&::before": { content: '""', position: "absolute", left: 0, right: 0, top: -8, height: 8 }
});

const Ingot = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  color: theme.kit.color.ink,
  fontSize: 12
}));

const Swatch = styled("span")(({ theme }) => ({
  width: 10,
  height: 10,
  borderRadius: 3,
  border: `1.5px solid ${theme.kit.color.shadow}`,
  flexShrink: 0
}));

const Count = styled(StatCounter)(({ theme }) => ({
  fontSize: 13,
  color: theme.kit.color.ink
}));

const Name = styled("span")(({ theme }) => ({
  color: theme.kit.color.inkDim,
  fontSize: 12
}));

/** Where a bucket is — the only caption the breakdown carries. */
const Where = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontSize: 10,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim,
  marginTop: 8
}));

const Quiet = styled("span")(({ theme }) => ({
  color: theme.kit.color.inkDim,
  fontSize: 12.5,
  lineHeight: 1.45
}));

const Chips = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: "4px 12px",
  margin: "4px 0 2px"
});

/**
 * A sentence in the expansion — the mock's `#stockX p`.
 *
 * The secondary voice, because the numbers above it are the reading and this
 * is what they add up to. What is emphasised inside it is `Strong`, its own
 * component rather than a `& b` rule here: a descendant selector outranks a
 * single class, so a nested rule would silently win.
 */
const Prose = styled("p")(({ theme }) => ({
  margin: "8px 0 0",
  color: theme.kit.color.inkDim,
  fontSize: 12.5,
  lineHeight: 1.45
}));

/** What the sentence is actually about — the count, in the primary ink. */
const Strong = styled("b")(({ theme }) => ({
  color: theme.kit.color.ink,
  fontWeight: 700
}));

/** A round swatch, for an army rather than a material. */
const Flag = styled(Swatch)({ borderRadius: "50%" });

/** The army's name, inheriting the pill's colour so the clock can tint it. */
const Army = styled("span")({ fontWeight: 700 });

/**
 * The watching row, which wraps.
 *
 * Four armies at one pill each runs past the middle of the screen and under
 * the turn row, which is the one piece of chrome the readouts must not reach.
 * Capped and wrapped rather than truncated: a watched game grows armies, and
 * silently dropping the fourth is the shelf's own bug (it capped at three
 * columns and clipped the rest into an implicit row).
 */
const WatchRow = styled(Row)({ flexWrap: "wrap", maxWidth: "60vw" });

/**
 * Lit while this army holds the clock — `TurnRow`'s `tone="brand"`.
 *
 * Built on `StockPill` rather than `Pill` so it keeps the focus ring: it is
 * focusable, because focusing it is how the expansion opens without a pointer.
 */
const ArmyPill = styled(StockPill, {
  shouldForwardProp: (prop: string) => prop !== "onClock"
})<{ onClock?: boolean }>(({ theme, onClock }) =>
  onClock ? { borderColor: theme.kit.color.brand, color: theme.kit.color.brand } : {}
);

/**
 * The two resting sentences (PLAN-UI §28.3, ruled at U3; U8's decision 5).
 *
 * The dock said these in two columns of prose that were always open, and a
 * shelf that is always open is always at its longest. Here they are the
 * *expansion* — the pill is the glance, this is the reading.
 *
 * `testid` rather than a fixed one: the watching readouts draw a panel per
 * army, and two of an id on a page is a smoke reading the one that is not on
 * screen.
 */
function ProseLines({ prose, testid }: { prose: ArmyReadout["prose"]; testid: string }) {
  return (
    <div data-testid={testid}>
      <Prose>
        <Strong>{prose.headline}</Strong>
        {prose.cooking}
        .
      </Prose>
      <Prose>{prose.work}.</Prose>
    </div>
  );
}

/** Nothing here is clickable, and the board underneath stays live. */
const ScorePill = styled(Pill)({ pointerEvents: "none" });

const Floats = styled("span")({ position: "relative", display: "inline-flex" });

const Score = styled(StatCounter)({
  fontSize: 15,
  animation: `${bump} 320ms ease-out`
});

const Gain = styled(StatCounter)(({ theme }) => ({
  position: "absolute",
  bottom: "100%",
  right: 0,
  fontSize: 14,
  lineHeight: 1,
  whiteSpace: "nowrap",
  color: theme.kit.color.cream,
  animation: `${rise} ${FLOAT_MS}ms ease-out both`
}));

/**
 * One material, as a swatch and a number.
 *
 * The colour rides in `style` rather than `sx` because it is **data**: it comes
 * off the board's own palette by line and grade, and `sx` mints a generated
 * class per distinct value.
 */
function Unit({
  unit,
  named
}: {
  unit: Holding;
  /** Say the material's name; the pill relies on the swatch alone. */
  named?: boolean;
}) {
  return (
    // **No `HoverCard` here, and both halves of the U6 ruling say so.** A
    // trigger that opens a panel gets no card while the panel is up — and
    // hovering *any* swatch in the pill is what opens the expansion, which
    // names every material six pixels below, so a card would land on the
    // answer it duplicates. Inside that expansion the word is already beside
    // the swatch (`named`), and a card in a 16px row pitch covers the row
    // under it. The swatch is the label; the panel is the card.
    <Ingot>
      <Swatch style={{ background: holdingTone(unit) }} />
      <Count>{unit.count}</Count>
      {named ? <Name>{unit.name}</Name> : null}
    </Ingot>
  );
}

/**
 * A row of named materials, silent when the bucket is empty.
 *
 * `where` is the one word that distinguishes the buckets — "on belts",
 * "banked" — and the first bucket, what your pieces hold, goes without one.
 */
function Bucket({ units, where }: { units: readonly Holding[]; where?: string }) {
  if (units.length === 0) return null;
  return (
    <>
      {where && <Where>{where}</Where>}
      <Chips>
        {units.map((unit) => (
          <Unit key={`${unit.line}-${unit.grade}`} unit={unit} named />
        ))}
      </Chips>
    </>
  );
}

/** Material a pill lists: the swatches, no names. */
const swatches = (units: readonly Holding[]) =>
  units.map((unit) => <Unit key={`${unit.line}-${unit.grade}`} unit={unit} />);

/**
 * What is on the floor, and how many warehouses hold the banked bucket.
 *
 * `ArmyReadout` does not carry these yet, and the board does: a unit lying
 * `loose` on a square and a structure owned by this side are both facts on
 * `SquareFacts`, so this counts them rather than asking the rules.
 */
function groundOf(facts: BoardFacts, side: Side): { loose: Holding[]; stores: number } {
  const loose = new Map<string, Holding>();
  let stores = 0;
  for (const square of facts.squares.values()) {
    if (square.structure?.owner === side) stores++;
    for (const unit of square.units) {
      if (unit.where !== "loose") continue;
      const key = `${unit.line}-${unit.grade}`;
      const held = loose.get(key);
      if (held) held.count++;
      else {
        const { material, line, grade, name } = unit;
        loose.set(key, { material, line, grade, count: 1, name });
      }
    }
  }
  return { loose: [...loose.values()], stores };
}

export function Readouts({
  scorePill,
  watching = false
}: {
  /** @deprecated The view carries the board; kept so the shell compiles until its line is updated. */
  state?: unknown;
  /** @deprecated Whose economy this is comes from the session's seat. */
  side?: unknown;
  /**
   * The `score-panel` flag. It gates the *pill* and nothing else: the score is
   * a `StageTally` field the reducer keeps on every board, so a player on
   * either side of the rollout is playing exactly the same game and their logs
   * replay identically.
   */
  scorePill: boolean;
  /**
   * Two machines are playing each other and nobody holds a seat.
   *
   * **A whole seat, not a player's readouts with the numbers missing** — the
   * exception §28.6 names. `shelfTotals` is scoped to a side and a watcher
   * holds none, so the stock pill would be a row of noughts claiming the
   * viewer owned no army; the score is the *run's* and a watched game has no
   * run to keep one for. So both go, and one pill per living army takes their
   * place: which of these factories is winning is the question a watched game
   * is actually being asked.
   */
  watching?: boolean;
}) {
  const root = useChromeRect("readouts", "top");

  // The view is rebuilt when the session's revision moves, so the facts read
  // in the same render are the board the readouts describe.
  const { readouts } = useSessionView();
  const { board } = useGame();
  const mine = readouts.mine;
  // Null outside a run; the floats want a number, and nought never rises.
  const score = readouts.score ?? 0;
  const floats = useScoreFloats(score);
  const ground = groundOf(board.facts(), mine.side);

  if (watching) {
    return (
      <WatchRow ref={root}>
        {readouts.armies.map((each) => (
          <StockWrap key={each.side}>
            <ArmyPill data-testid={`watch-army-${each.side}`} tabIndex={0} onClock={each.onClock}>
              <Flag style={{ background: armyTone(each.side) }} />
              <Army>{each.name}</Army>
              {swatches(each.held)}
              {/* The sentence the dock said about a watched army, and
                  nothing else on the screen counts one: the have-row was a
                  player's and the board is too big to count by eye. */}
              <PillLabel>
                {each.pieces} pieces · {each.belts} belts
              </PillLabel>
            </ArmyPill>

            <Detail className="stock-detail" data-testid={`watch-detail-${each.side}`}>
              <Bucket units={each.held} />
              <Bucket units={each.onBelts} where="on belts" />
              {/* Tiles are nought in a watched army's prose, and the engine
                  says so: a Free Space tile is held by a *run*, which a watched
                  game is not. */}
              <ProseLines prose={each.prose} testid={`watch-prose-${each.side}`} />
            </Detail>
          </StockWrap>
        ))}
      </WatchRow>
    );
  }

  return (
    <Row ref={root}>
      <StockWrap>
        {/* No caption. The swatches are the label — a pill reading "STOCK" in
            front of four numbers was a word spent on what the numbers already
            said (designer ruling, U3 review). Hover or focus opens the
            expansion below, and that is where each one is named. */}
        <StockPill data-testid="stock" tabIndex={0}>
          {/* The glance: everything you could spend, wherever it is parked. */}
          {mine.stock.length > 0 ? swatches(mine.stock) : <Quiet>nothing yet</Quiet>}
        </StockPill>

        {/* **The numbers, then what they add up to** (U8's decision 5): the
            dock's two resting columns are these two sentences, and this is
            where they landed. The artifacts-in-transit line folded into the
            first of them — it was a sentence of its own saying the same thing
            in more words.

            Its own test ids, never the shelf's `units-<slot>`: two of one id on
            the page is a smoke clicking the one that is off screen. */}
        <Detail className="stock-detail" data-testid="stock-detail">
          <Bucket units={mine.held} />
          <Bucket units={mine.onBelts} where="on belts" />
          <Bucket
            units={mine.stored}
            where={`banked in ${ground.stores} store${ground.stores === 1 ? "" : "s"}`}
          />
          <Bucket units={ground.loose} where="on the ground" />
          <ProseLines prose={mine.prose} testid="stock-prose" />
        </Detail>
      </StockWrap>

      {/* The run's running total. Campaign only: a tally exists in a scenario
          too, but a scenario is a board set up to show one thing and a running
          total is a claim about a whole game.

          **`aria-label`, not `title`**: this pill takes no pointer at all
          (`pointerEvents: none`, so the board underneath stays live), which
          means a native tooltip on it could never have appeared and a
          `HoverCard` could never open either. The word was always the
          accessible name and is now said as one. */}
      {readouts.score !== null && scorePill && (
        <ScorePill data-testid="score-panel" aria-label="Score">
          {/* The `+N`s go over the number rather than beside it: the pill is
              pinned to a corner, so anything laid out beside the total changes
              the row's width and shoves it sideways as it plays. */}
          <Floats>
            <Score
              data-testid="score-value"
              // **Keyed on the score**, which is what replays the animation: a
              // CSS animation runs once per mount, so an unkeyed span bumps on
              // the first capture of the run and never again.
              key={score}
            >
              {score}
            </Score>
            {floats.map((one) => (
              <Gain key={one.id} data-testid="score-gain">
                +{one.amount}
              </Gain>
            ))}
          </Floats>
        </ScorePill>
      )}
    </Row>
  );
}
